import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";

import { Workout } from "../Workout/Workout";
import { ChangeDateModal } from "@/features/change-date-modal";

import startIcon from "@/assets/img/start-icon.png";
import changeDateIcon from "@/assets/clock.png";
import changeIcon from "@/assets/change.png";

import { DEVELOP_START_LINK, PROD_START_LINK } from "@/constants/links";
import { tg } from "@/constants/telegram";
import { useEditTrainingDateMutation } from "@/store/trainingsApi";
import { Exercise } from "../model";
import { ActionButton, ActionButtonVariant } from "@/shared/ui";
import { closeTG, formatToYMD } from "@/shared/utils";
import { useYandexMetrika, GOALS } from "@/hooks/useYandexMetrika";

type WorkoutsProps = {
  exercizes: Exercise[];
  trainingId: number;
  oldDate: string;
  isDevelop: boolean;
  isFixed?: boolean;
  isFree?: boolean;
  disabled?: boolean;
  hasActiveSubscription?: boolean;
  trainingIndex?: number;
  trainingCount?: number;
  allowedTrainingIds?: number[];
  isEditMode?: boolean;
  onRemoveExercise?: (exerciseId: number) => void;
  onEditExercise?: (exerciseId: number) => void;
};

export const Workouts: React.FC<WorkoutsProps> = ({
  exercizes,
  trainingId,
  oldDate,
  isDevelop,
  isFixed,
  isFree,
  disabled,
  hasActiveSubscription = false,
  trainingIndex = -1,
  trainingCount = 0,
  allowedTrainingIds,
  isEditMode = false,
  onRemoveExercise,
  onEditExercise,
}) => {
  const allowedIdsSet = useMemo(() => {
    if (!allowedTrainingIds || allowedTrainingIds.length === 0) {
      return null;
    }
    return new Set(allowedTrainingIds);
  }, [allowedTrainingIds]);

  const isIncludedInAllowedCount =
    allowedIdsSet !== null
      ? allowedIdsSet.has(trainingId)
      : trainingIndex >= 0 && trainingIndex < Math.max(3, trainingCount);
  const [initData, setInitData] = useState();
  const firstExercise = exercizes[0];
  const navigate = useNavigate();
  const { trackButtonClick, trackAction } = useYandexMetrika();

  const [startLink, setStartLink] = useState<string>();

  useEffect(() => {
    if (!isFree) {
      setStartLink(
        `${PROD_START_LINK}?start=exercize-${trainingId}-${firstExercise?.id}`
      );
    } else {
      setStartLink(
        `${PROD_START_LINK}?start=exercize-free${trainingId}-${firstExercise?.id}`
      );
    }
  }, [firstExercise?.id, isFree, trainingId]);

  const changeTrainDate = isDevelop
    ? `${DEVELOP_START_LINK}?start=EditSample`
    : `${PROD_START_LINK}?start=EditSample`;

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editTrainingDate, { isLoading }] = useEditTrainingDateMutation();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedDate) {
      const formattedDate = formatToYMD(selectedDate);
      const oldFormattedDate = formatToYMD(oldDate);

      await editTrainingDate({
        trainig_id: trainingId,
        old_date: oldFormattedDate,
        new_date: formattedDate,
      });
      setIsModalOpen(false);
      toast.success("Дата успешно обновлена");
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
  };

  useEffect(() => {
    if (!initData && tg?.initData) {
      setInitData(tg.initData);
    }
  }, [initData]);

  const handleStartClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Отслеживаем нажатие на кнопку "Старт"
    trackButtonClick(GOALS.BUTTON_START, {
      trainingId,
      hasActiveSubscription,
      isFree
    });
    
    // Check if user needs subscription
    if (!hasActiveSubscription && !isIncludedInAllowedCount) {
      navigate("/subscription");
      return;
    }
    
    // Если подписка активна или это входит в первые 5 тренировок, переходим по ссылке
    if (startLink) {
      // Отслеживаем начало тренировки
      trackAction(GOALS.ACTION_TRAINING_STARTED, {
        trainingId,
        firstExerciseId: firstExercise?.id
      });
      
      window.location.href = startLink;
      closeTG();
    }
  };

  const handleStartClickWrapper = () => {
    // Создаем синтетическое событие для совместимости
    const syntheticEvent = {
      preventDefault: () => {},
    } as React.MouseEvent;
    
    handleStartClick(syntheticEvent);
  };

  return (
    <div className="workouts">
      {isModalOpen && (
        <ChangeDateModal
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleSubmit}
          isLoading={isLoading}
        />
      )}

      {/* Скрываем кнопку "Старт" для тренировок, созданных пользователем */}
      {!disabled && !isFree && (
        <ActionButton
          label="Старт"
          icon={startIcon}
          to={startLink || "#"}
          onClick={handleStartClickWrapper}
        />
      )}
      {!isFixed && (
        <>
          <ActionButton
            label="Перенести"
            icon={changeDateIcon}
            onClick={() => {
              trackButtonClick(GOALS.BUTTON_MOVE_TRAINING, { trainingId });
              setIsModalOpen(true);
            }}
            variant={ActionButtonVariant.Move}
          />
        </>
      )}

      {exercizes.map((item: Exercise, index: number) => (
        <div key={index} style={{ position: "relative" }}>
          {isEditMode && (
            <div
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                zIndex: 10,
                display: "flex",
                gap: "8px",
              }}
            >
              {onEditExercise && (
                <button
                  onClick={() => onEditExercise(item.id)}
                  style={{
                    backgroundColor: "#667eea",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    fontSize: "16px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                  }}
                  title="Редактировать упражнение"
                >
                  ✎
                </button>
              )}
              {onRemoveExercise && (
                <button
                  onClick={() => onRemoveExercise(item.id)}
                  style={{
                    backgroundColor: "#ff4b38",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    fontSize: "20px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                  }}
                  title="Удалить упражнение"
                >
                  ×
                </button>
              )}
            </div>
          )}
          <Workout
            describtion={String(item.description)}
            title={item.title}
            image={item.title_photo}
            id={item.id}
            trainingId={trainingId}
            isDevelop={isDevelop}
            duration={item.duration}
            isFixed={isFixed}
            disabled={disabled || isEditMode}
            documentation={item.documentation}
            muscleGroup={item.muscle_group}
            isFree={isFree}
            hasActiveSubscription={hasActiveSubscription}
            isIncludedInAllowedCount={isIncludedInAllowedCount}
            results={item.results}
            config={item.config}
          />
        </div>
      ))}
    </div>
  );
};
